import React from "react";
import { Box, Typography, Stack, Paper, Button, Collapse } from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AiHelpSection from "../components/guide/AiHelpSection";

const Step0Onboarding = ({ onComplete, onOpenAssistant }) => {
  // --- Instruction cards content -------------------------------------------
  const cards = [
    {
      title: "1. What this agent does",
      body: (
        <Stack spacing={1.5}>
          <Typography variant="body1">
            Hi <strong>Facilitator</strong>, this tool guides you through a few questions to help you reflect on the learner’s context, the learner’s organisation and the learner’s needs.
          </Typography>
          <Typography variant="body1">
            The learner can be one single person or an entire group.
          </Typography>
        </Stack>
      ),
    },
    {
      title: "2. How it works",
      body: (
        <Stack spacing={1.5}>
          <Typography variant="body1">
            Your answers are the input for an LLM to suggest a customized sequence of MLUs from a full catalogue of <strong>72 MLUs</strong>.
          </Typography>
          <Typography variant="body1">
            The more clear, detailed and honest you are, the better the results can be. Take your time!
          </Typography>
        </Stack>
      ),
    },
    {
      title: "3. The AI Agent and your responsibility",
      body: (
        <Stack spacing={2}>
          <Typography variant="body1">
            You are interacting with an <strong>AI Agent</strong>:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
              <li>The tool is aware of the learning program concepts created by K8 and approved by the T4R consortium.</li>
              <li>This tool uses a Large Language Model to generate learning journey suggestions.</li>
              <li>These are <strong>recommendations, not decisions</strong>.</li>
              <li>Please <strong>review and customise</strong> them.</li>
            </ul>
          </Typography>
        </Stack>
      ),
    },
  ];

  const disclaimer = (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2,
          borderRadius: 2,
          bgcolor: "rgba(0, 0, 0, 0.03)",
          borderLeft: "6px solid",
          borderColor: "grey.600",
          width: "fit-content",
        }}
      >
        <VerifiedUserOutlinedIcon sx={{ fontSize: 28, color: "grey.700" }} />
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          You remain responsible for the final learning journey.
        </Typography>
      </Paper>
    </Box>
  );

  const TOTAL_CARDS = cards.length;

  // Array tracking whether each instruction card has been read
  const [readStatus, setReadStatus] = React.useState(Array(TOTAL_CARDS).fill(false));
  const [expandedCard, setExpandedCard] = React.useState(-1);

  const toggleRead = (index) => {
    setReadStatus((prev) => {
      const newStatus = [...prev];
      const isCurrentlyRead = newStatus[index];
      newStatus[index] = !isCurrentlyRead;

      // Auto-collapse current and expand next if marking as read
      if (!isCurrentlyRead) {
        const nextUnread = newStatus.findIndex((status) => status === false);
        setExpandedCard(nextUnread); // will be -1 if all are read
      }

      return newStatus;
    });
  };

  const handleAccordionToggle = (index) => {
    setExpandedCard((prev) => (prev === index ? -1 : index));
  };

  // Notify parent whenever the overall completion status changes
  React.useEffect(() => {
    if (onComplete) {
      const allRead = readStatus.every((status) => status === true);
      onComplete(allRead);
    }
  }, [readStatus, onComplete]);

  return (
    <Box sx={{ maxWidth: { xs: 800, md: 1000 }, mx: "auto", mt: 2, px: { xs: 2, md: 4 } }}>
      {/* Main title */}
      <Typography
        variant="h4"
        fontWeight={600}
        gutterBottom
        color="primary"
        textAlign="center"
      >
        Welcome to T4R Learning Journey Design Agent
      </Typography>

      <Typography
        variant="subtitle1"
        color="text.secondary"
        textAlign="center"
        sx={{ mb: 2 }}
      >
        Your K8 designed AI Agent for shaping meaningful, context-aware learning
        journeys.
      </Typography>

      {/* "Start Reading" button to initially expand the first card */}
      {expandedCard === -1 && readStatus.every(s => !s) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button variant="contained" size="large" onClick={() => setExpandedCard(0)}>
            Start Reading Instructions
          </Button>
        </Box>
      )}

      {/* Cards: mapped completely out so they are all visible */}
      <Stack spacing={2} sx={{ mb: 4 }}>
        {cards.map((card, index) => {
          const isRead = readStatus[index];
          const isExpanded = expandedCard === index;

          return (
            <Paper
              key={card.title}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: isRead ? "success.main" : (isExpanded ? "primary.main" : "divider"),
                backgroundColor: isRead
                  ? "rgba(46, 125, 50, 0.04)"
                  : "background.default",
                transition: "all 0.3s ease",
                overflow: "hidden"
              }}
            >
              {/* Clickable Card Header */}
              <Box
                onClick={() => handleAccordionToggle(index)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" }
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ mb: 0 }}>
                    {card.title}
                  </Typography>
                  {card.subtitle && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {card.subtitle}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  {isRead ? (
                    <>
                      ✓
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                        Read
                      </Box>
                    </>
                  ) : (
                    isExpanded ? "Collapse" : "Expand"
                  )}
                </Typography>
              </Box>

              {/* Collapsible Card Body */}
              <Collapse in={isExpanded}>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Box sx={{ mb: 2 }}>{card.body}</Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      variant={isRead ? "contained" : "outlined"}
                      color={isRead ? "success" : "primary"}
                      onClick={(e) => {
                        e.stopPropagation(); // prevent accordion toggle from firing
                        toggleRead(index);
                      }}
                      size="small"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {isRead ? (
                        <>
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, mr: 0.5 }}>
                            Read
                          </Box>
                          ✓
                        </>
                      ) : (
                        "Mark as read"
                      )}
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Stack>

      {/* Optional AI help: always visible below the instructions, collapsed until opened */}
      <AiHelpSection onOpenAssistant={onOpenAssistant} />

      {disclaimer}
    </Box>
  );
};

export default Step0Onboarding;
